import { FC, useContext, useState } from "react"
import PublicAvatarImage from "./PublicAvatarImage"
import PublicAvatarVideo from "./PublicAvatarVideo"
import ShareModal from "./ShareModal"
import useLoading from "../../hooks/useLoading"
import { AuthContext } from "../auth/context"
import { Button } from "../ui/button"
import { CoachPublicProfileContext } from "../../pages/manage-business/profile/context/coachPublicProfileContext"
import { ContactRequests } from "../../api/app.service"
import { getTextColor } from "../../utils/getTextColor"
import { isCoachInCommissionTracking } from "../../utils/commissionTracking"
import { ProfileMessageForm } from "../auth/register-form/profile-message-form"
import { RegisterFormProvider } from "../auth/register-form/context"
import { SvgChat } from "../icons"
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser"
import { useAuthModal } from "../auth/use-auth-modal"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

const PublicProfileInformation: FC = () => {
  const location = useLocation()
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const { showRegister } = useAuthModal()
  const { user, setUser } = useContext(UserCtx)
  const isAuthed = useAuthenticatedUser(user)
  const navigate = useNavigate()
  const { coachPublicProfileData, isOwner } = useContext(
    CoachPublicProfileContext
  )
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false)
  const coachColor = coachPublicProfileData?.profile_information?.profileColor
  const coachTitle = coachPublicProfileData?.profile_information?.title
  const coachRegion = coachPublicProfileData?.profile_information?.region?.label
  const endPoint = coachPublicProfileData?.endpoint_slug
  const response: string = coachPublicProfileData.response_time
  const [profileMessageFormModal, setprofileMessageFormModal] =
    useState<boolean>(false)

  const { email, firstName, lastName, accountType, password, phone } =
    useContext(AuthContext)

  const connectAndRedirect = async (user: any) => {
    try {
      await ContactRequests.createContactConnection({
        sender_profile_id: coachPublicProfileData?.coach_profile_id,
        receiver_profile_id: user.activeProfileId,
        user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
        status: user?.isCoach
          ? "c2c"
          : isCoachInCommissionTracking(coachPublicProfileData?.endpoint_slug)
          ? // if coach is connecting to a coach then change the status to c2c.
            // idk if user.isCoach is a correct condition to work on. Recheck later.
            "zoee lead"
          : "connected",
      })

      user?.isCoach
        ? navigate(
            `/coach/inbox?chat=${coachPublicProfileData?.coach_profile_id}`
          )
        : navigate(
            `/member/inbox?chat=${coachPublicProfileData?.coach_profile_id}`
          )
    } catch (error: any) {
      console.error(error)
      toast.error(error.response?.data?.message || "An error occurred")
    } finally {
      stopLoading()
    }
  }

  const responseTime = () => {
    if (response === "Out of Office") {
      return (
        <p className="text-graySlate text-center text-base">Out of Office</p>
      )
    } else if (response !== "Don't show response time" && response !== null) {
      return (
        <p className="text-graySlate text-base">
          Response time: Within {response}
        </p>
      )
    } else if (response === null) {
      return <></>
    } else {
      return <></>
    }
  }

  const authenticateAndMessage = async () => {
    localStorage.setItem(
      "coachRedirect",
      `/coach/inbox?chat=${coachPublicProfileData?.coach_profile_id}&redirect=${location.pathname}${location.search}`
    )
    localStorage.setItem(
      "memberRedirect",
      `/member/inbox?chat=${coachPublicProfileData?.coach_profile_id}&redirect=${location.pathname}${location.search}`
    )

    if (isOwner) {
      toast.error(
        "Can't take action on profile page when logged into this account"
      )
      return
    }

    if (isAuthed) {
      startLoading()
      await connectAndRedirect(user)
    } else {
      showRegister({
        accountType: "member",
        onSuccess: async (user) => {
          await connectAndRedirect(user)
        },
      })
    }
  }

  return (
    <RegisterFormProvider
      defaultEmail={email}
      defaultAccountType={accountType}
      onSuccess={() => true}
      defaultPhone={phone}
      defaultFirstName={firstName}
      defaultLastName={lastName}
      defaultPassword={password}
    >
      {/* DESKTOP */}
      <div className="relative hidden w-full items-center gap-[20px] px-12 md:flex">
        {/* PROFILE IMAGE OR VIDEO */}
        <div className="min-w-[185px] max-w-[185px]">
          {coachPublicProfileData?.has_profile_video ? (
            <div className="h-[258px] w-full">
              <PublicAvatarVideo />
            </div>
          ) : (
            <div className="h-[185px] w-full md:-mt-44">
              <PublicAvatarImage
                coachPublicProfileData={coachPublicProfileData}
              />
            </div>
          )}
        </div>

        {/* INFORMATION */}

        <div className="hidden w-full flex-col items-start md:flex lg:-mt-4 lg:ml-8 ">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col gap-[1px]">
              <h3 className="text-[28px] font-bold">
                {`${coachPublicProfileData?.first_name} ${coachPublicProfileData?.last_name}`}
              </h3>
              <p className="mb-[17px] text-[16px]  md:max-w-[90%]">
                {coachTitle || "Coach"}
              </p>
            </div>
            <div className="-mb-4 flex flex-col gap-2">
              <Button
                variant="ghost"
                style={{
                  backgroundColor: coachColor,
                  color: getTextColor(coachColor),
                }}
                onClick={() => {
                  authenticateAndMessage()
                }}
              >
                <SvgChat />
                Send Message
              </Button>
              {/* Show response time */}
              <>{responseTime()}</>
            </div>
            {profileMessageFormModal && (
              <ProfileMessageForm
                showConfirm={profileMessageFormModal}
                setShowConfirm={setprofileMessageFormModal}
                coachName={`${coachPublicProfileData?.first_name} ${coachPublicProfileData?.last_name}`}
                connectAndRedirect={connectAndRedirect}
              />
            )}
          </div>
          <div className="md:max-w-[80%]">
            <p className="mb-[17px] text-[16px] md:max-w-[80%]">
              {coachPublicProfileData?.profile_information?.description}
            </p>
          </div>
          <div className="flex gap-[48px]">
            {coachPublicProfileData?.profile_information?.yearsExperience >
              0 && (
              <div className="flex flex-col items-start gap-[8px]">
                <p className="text-graySlate text-base">Experience</p>
                <p className="text-[16px] font-bold">
                  {coachPublicProfileData?.profile_information?.yearsExperience}{" "}
                  Years
                </p>
              </div>
            )}
            {coachRegion && (
              <div className="flex flex-col items-start gap-[8px]">
                <p className="text-graySlate text-base">Location</p>
                <p className="text-[16px] font-bold">{coachRegion}</p>
              </div>
            )}

            {coachPublicProfileData?.credentials?.credentials.length > 0 ? (
              <div className="flex flex-col items-start gap-[8px]">
                <p className="text-graySlate text-base">Credentials</p>
                <p className="text-[16px] font-bold">
                  {coachPublicProfileData.credentials.credentials[0].name}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {shareModalOpen && (
        <ShareModal
          endpoint_slug={endPoint}
          shareModalOpen={shareModalOpen}
          setShareModalOpen={setShareModalOpen}
        />
      )}
      {/* MOBILE */}
      <div className="relative flex w-full flex-col items-start gap-[20px] md:hidden">
        {/* PROFILE IMAGE OR VIDEO */}
        <div className="-mt-8 mb-28 flex w-full items-center justify-start gap-[16px]">
          <div className="h-[130px] w-[130px]">
            {coachPublicProfileData?.has_profile_video ? (
              <div className="h-[258px] min-w-full max-w-full">
                <PublicAvatarVideo />
              </div>
            ) : (
              <div className="h-[130px] w-[130px]">
                <PublicAvatarImage
                  coachPublicProfileData={coachPublicProfileData}
                />
              </div>
            )}
          </div>
          <div className="ml-auto flex flex-col">
            <Button
              variant="ghost"
              style={{
                backgroundColor: coachColor,
                color: getTextColor(coachColor),
              }}
              onClick={authenticateAndMessage}
            >
              <SvgChat />
              Message
            </Button>
            <>{responseTime()}</>
          </div>
        </div>

        <div className="flex flex-col items-start justify-start">
          <h3 className="text-[22px] font-bold">{`${coachPublicProfileData?.first_name} ${coachPublicProfileData?.last_name}`}</h3>

          <p className="text-[16px]">
            {coachPublicProfileData?.profile_information?.title || "Coach"}
          </p>
        </div>
        {/* INFORMATION */}
        <div className="flex flex-col items-start">
          <p className="mb-[17px] text-[16px]">
            {coachPublicProfileData?.profile_information?.description}
          </p>
        </div>
      </div>
    </RegisterFormProvider>
  )
}

export default PublicProfileInformation
